import { useEffect, useRef, useState, useCallback } from "react";

export default function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [hasIntersected, setHasIntersected] = useState(false);

  const checkVisibility = useCallback(() => {
    if (hasIntersected || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    // Trigger when element is within 100px of the viewport
    if (rect.top < windowHeight + 100 && rect.bottom > -100) {
      setHasIntersected(true);
    }
  }, [hasIntersected]);

  useEffect(() => {
    // Primary: IntersectionObserver with generous rootMargin
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      { threshold: 0, rootMargin: "100px 0px" }
    );

    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);

    // Fallback: scroll-based check for mobile (Lenis may block IO)
    window.addEventListener("scroll", checkVisibility, { passive: true });
    // Also check on initial load (elements already in view)
    checkVisibility();

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      window.removeEventListener("scroll", checkVisibility);
    };
  }, [threshold, hasIntersected, checkVisibility]);

  return [ref, hasIntersected];
}
